import {RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

const CHAT_THREAD_BOTTOM_PIN_PX = 32;
const CHAT_THREAD_FOLLOW_RESUME_PX = 8;
const CHAT_THREAD_FOLLOW_RELEASE_PX = 48;

type UseChatScrollOptions = {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  isStreaming?: boolean;
  hasMessages?: boolean;
  /** Serialized fingerprint of content that should trigger stick-to-bottom remeasure. */
  contentVersion?: string | number;
};

export function useChatScroll({
  scrollContainerRef,
  messagesEndRef,
  isStreaming = false,
  hasMessages = false,
  contentVersion = 0,
}: UseChatScrollOptions) {
  const [scrollToBottomNonce, setScrollToBottomNonce] = useState(0);
  const [threadEndInView, setThreadEndInView] = useState(true);
  const [unseenStreamTail, setUnseenStreamTail] = useState(false);
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);

  const pinnedToBottomRef = useRef(true);
  const followStreamTailRef = useRef(true);
  const programmaticThreadScrollRef = useRef(false);
  const prevThreadScrollHeightRef = useRef(0);
  const didInitialScrollRef = useRef(false);
  const lastScrollTopRef = useRef(0);

  const scrollThreadToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    programmaticThreadScrollRef.current = true;
    el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
    lastScrollTopRef.current = el.scrollTop;
    pinnedToBottomRef.current = true;
    followStreamTailRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        programmaticThreadScrollRef.current = false;
      });
    });
  }, [scrollContainerRef]);

  const requestScrollToBottom = useCallback(() => {
    pinnedToBottomRef.current = true;
    followStreamTailRef.current = true;
    setScrollToBottomNonce((n) => n + 1);
  }, []);

  // On first mount, jump to the bottom so reopening an existing thread always
  // starts pinned to the latest message (the scroll viewport otherwise starts
  // at the top). The nonce-driven effect below retries across layout frames.
  useLayoutEffect(() => {
    if (didInitialScrollRef.current || !hasMessages) {
      return;
    }
    didInitialScrollRef.current = true;
    requestScrollToBottom();
  }, [hasMessages, requestScrollToBottom]);

  const syncPinnedToBottomFromScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const prevTop = lastScrollTopRef.current;
    lastScrollTopRef.current = el.scrollTop;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottomRef.current = distance <= CHAT_THREAD_BOTTOM_PIN_PX;
    // Reveal a soft fade under the header once content has scrolled out of view
    // above the fold (mirrors the bottom fade affordance).
    setIsScrolledFromTop(el.scrollTop > 1);
    if (programmaticThreadScrollRef.current) {
      return;
    }
    // Any deliberate upward scroll releases tail-following immediately, so a fast
    // stream can't keep yanking the reader back down before they clear the
    // distance-based release threshold below. Growing content never fires a
    // scroll event, so direction is a reliable signal of user intent here.
    if (el.scrollTop < prevTop - 1 && distance > CHAT_THREAD_FOLLOW_RESUME_PX) {
      followStreamTailRef.current = false;
    } else if (distance > CHAT_THREAD_FOLLOW_RELEASE_PX) {
      followStreamTailRef.current = false;
    } else if (distance <= CHAT_THREAD_FOLLOW_RESUME_PX) {
      followStreamTailRef.current = true;
    }
  }, [scrollContainerRef]);

  useLayoutEffect(() => {
    if (scrollToBottomNonce === 0) {
      return;
    }
    let cancelled = false;
    const run = () => {
      if (cancelled) {
        return;
      }
      scrollThreadToBottom();
    };
    run();
    const raf = requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
    const scrollRetryTimeoutId = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(scrollRetryTimeoutId);
    };
  }, [scrollToBottomNonce, scrollThreadToBottom]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => syncPinnedToBottomFromScroll();
    el.addEventListener('scroll', onScroll, {passive: true});
    syncPinnedToBottomFromScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollContainerRef, syncPinnedToBottomFromScroll]);

  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }

    const prevH = prevThreadScrollHeightRef.current;
    const h = el.scrollHeight;
    const bottomEdge = el.scrollTop + el.clientHeight;
    const wasPinnedToContentBottom =
      prevH > 0 && bottomEdge >= prevH - CHAT_THREAD_FOLLOW_RELEASE_PX;
    const distanceNow = h - el.scrollTop - el.clientHeight;
    const atBottomNow = distanceNow <= CHAT_THREAD_FOLLOW_RELEASE_PX;
    // Only re-pin to the tail if the reader hasn't scrolled away. Without this
    // guard a streaming response keeps forcing the viewport down even after the
    // user scrolls up to read earlier content.
    const shouldStickToTail =
      followStreamTailRef.current && (atBottomNow || wasPinnedToContentBottom);

    if (shouldStickToTail) {
      scrollThreadToBottom();
    } else {
      followStreamTailRef.current = false;
    }

    const elAfter = scrollContainerRef.current;
    if (elAfter) {
      prevThreadScrollHeightRef.current = elAfter.scrollHeight;
    }
  }, [contentVersion, scrollThreadToBottom, scrollContainerRef]);

  useLayoutEffect(() => {
    if (!isStreaming) {
      return;
    }
    if (!followStreamTailRef.current) {
      setUnseenStreamTail(true);
    }
  }, [isStreaming, contentVersion]);

  useEffect(() => {
    if (!isStreaming) {
      setUnseenStreamTail(false);
      return;
    }
    if (threadEndInView) {
      setUnseenStreamTail(false);
    }
  }, [isStreaming, threadEndInView]);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const target = messagesEndRef.current;
    if (!root || !target) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setThreadEndInView(entry.isIntersecting);
      },
      {root, rootMargin: '0px 0px 12px 0px', threshold: 0}
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [contentVersion, isStreaming, scrollContainerRef, messagesEndRef]);

  const showBelowFoldHint = !threadEndInView && (isStreaming ? unseenStreamTail : hasMessages);

  return {
    scrollThreadToBottom,
    requestScrollToBottom,
    showBelowFoldHint,
    showAboveFoldHint: isScrolledFromTop,
  };
}
